import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle, ArrowRight, Shield, Zap, ChevronDown } from 'lucide-react'

const WHO = [
  { label: 'Freelancers', detail: 'Consultants, designers, developers, writers' },
  { label: 'Drivers', detail: 'Uber, Bolt, Amazon Flex, delivery couriers' },
  { label: 'Tradespeople', detail: 'Plasterers, plumbers, electricians, builders' },
  { label: 'Landlords', detail: 'Rental income, property expenses, mortgage interest' },
  { label: 'Online sellers', detail: 'Amazon, eBay, Etsy, Shopify' },
  { label: 'Consultants', detail: 'Self-employed service businesses of all kinds' },
]

const FEATURES = [
  'Connect your bank account',
  'Import transactions automatically',
  'Categorise transactions in-app',
  'Smarter suggestions based on your past choices',
  'Track income, expenses and tax position',
  'Attach receipts and keep records tidy',
  'Live tax estimate — updated in real time',
  'Every HMRC deadline in one calendar',
  'MTD-ready quarterly submission workflow',
  'Full audit trail on every decision',
]

const FAQS = [
  {
    q: 'Is Quarterly built for UK sole traders?',
    a: "Yes. Quarterly is designed specifically for UK sole traders, freelancers and landlords. Every tax rate, allowance and HMRC rule is built in — from mileage rates to the 50% meals limit. It is not a generic bookkeeping tool adapted for the UK.",
  },
  {
    q: 'Does Quarterly connect to my bank?',
    a: "Yes. Quarterly uses Open Banking to connect to over 50 UK banks and credit cards. The connection is read-only — we can see your transactions, we can never move your money. Your transactions import automatically once you're connected.",
  },
  {
    q: 'Does Quarterly replace my accountant?',
    a: "Quarterly is not a replacement for an accountant, and we would not claim it is. What it does is handle the repetitive admin — importing transactions, suggesting categories, tracking your tax position — so that if you do work with an accountant, their time is spent on judgment calls rather than data entry.",
  },
  {
    q: 'Can I categorise transactions in the app?',
    a: "That is the core of the product. You review each transaction, mark it as business or personal, and assign a category. Quarterly AI suggests the most likely category based on the merchant and your previous choices. You confirm. Most people get through a full quarter in under 20 minutes.",
  },
  {
    q: 'Is Quarterly built for Making Tax Digital?',
    a: "Yes. MTD ITSA is the reason this product exists. From April 2026, sole traders earning over £50,000 must file quarterly digital updates to HMRC. Quarterly is built around that workflow from the ground up — not retrofitted.",
  },
  {
    q: 'What happens when a transaction needs manual review?',
    a: "Quarterly flags anything it cannot categorise with high confidence. You can review these separately, adjust the suggested category, or leave a note. Everything is logged so you have a full audit trail.",
  },
  {
    q: 'Can I cancel anytime?',
    a: "Yes. No lock-in, no cancellation fees. Cancel from your account settings at any time and your subscription ends at the next billing date.",
  },
]

export default function Pricing() {
  return (
    <div style={{ background: 'var(--navy)', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* Nav */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(8,18,31,0.94)', backdropFilter: 'blur(18px)',
        borderBottom: '1px solid var(--navy-border)',
        padding: '0 clamp(16px,4vw,40px)', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily:'var(--font-display)', fontSize:15, color:'#08121F', fontWeight:700 }}>Q</span>
          </div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--white)' }}>Quarterly</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Link to="/login" style={{ fontSize: 13, color: 'var(--slate)', fontWeight: 500 }}>Sign in</Link>
          <Link to="/login" className="btn btn-primary btn-sm">Get started</Link>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ paddingTop: 'clamp(96px,12vw,128px)', paddingBottom: 'clamp(32px,5vw,56px)', padding: 'clamp(96px,12vw,128px) clamp(16px,4vw,40px) clamp(32px,5vw,56px)', maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(0,212,170,.07)', border: '1px solid rgba(0,212,170,.18)', borderRadius: 20, padding: '5px 14px', fontSize: 12, color: 'var(--teal)', fontWeight: 600, marginBottom: 24 }}>
          <Shield size={12} color="var(--teal)" />
          Built by Chartered Accountants
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px,7vw,58px)', lineHeight: 1.08, color: 'var(--white)', marginBottom: 16, letterSpacing: '-.02em' }}>
          Simple pricing for<br />
          <span style={{ color: 'var(--teal)', fontStyle: 'italic' }}>sole traders.</span>
        </h1>
        <p style={{ fontSize: 'clamp(14px,1.8vw,17px)', color: 'var(--slate)', lineHeight: 1.7, maxWidth: 520, margin: '0 auto' }}>
          Everything you need to categorise transactions, track your tax position and stay ready for Making Tax Digital. No complex tiers. No accountancy software learning curve.
        </p>
      </section>

      {/* Pricing card */}
      <section style={{ padding: '0 clamp(16px,4vw,40px) clamp(48px,7vw,80px)', maxWidth: 640, margin: '0 auto' }}>
        <div style={{
          background: 'var(--navy-card)',
          border: '1px solid rgba(0,212,170,.25)',
          borderRadius: 18,
          padding: 'clamp(24px,4vw,40px)',
          position: 'relative',
          boxShadow: '0 0 60px rgba(0,212,170,.06)',
        }}>
          {/* Top tag */}
          <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: 'var(--teal)', borderRadius: 20, padding: '4px 16px', fontSize: 11, color: '#08121F', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', whiteSpace: 'nowrap' }}>
            One plan. Everything included.
          </div>

          {/* Plan name + price */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--white)', marginBottom: 4 }}>Quarterly</div>
              <div style={{ fontSize: 13, color: 'var(--slate)' }}>For UK sole traders and freelancers</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 44, color: 'var(--white)', lineHeight: 1 }}>£14.99</span>
                <span style={{ fontSize: 13, color: 'var(--slate)' }}>/month</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--slate-dim)', marginTop: 3 }}>Cancel anytime</div>
            </div>
          </div>

          <div style={{ height: 1, background: 'var(--navy-border)', margin: '20px 0' }} />

          {/* Features list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
            {FEATURES.map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckCircle size={14} color="var(--teal)" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 13.5, color: 'var(--off-white)' }}>{f}</span>
              </div>
            ))}
          </div>

          <Link to="/login" className="btn btn-primary btn-lg btn-full" style={{ justifyContent: 'center', fontSize: 16, gap: 9 }}>
            Start for £14.99/month <ArrowRight size={16} />
          </Link>

          <div style={{ textAlign: 'center', marginTop: 14, fontSize: 12, color: 'var(--slate-dim)' }}>
            No accounting software learning curve. Cancel anytime.
          </div>
        </div>

        {/* Trust marks below card */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 20, flexWrap: 'wrap' }}>
          {['Chartered Accountants on the team', 'Built to HMRC MTD specification', 'MTD ITSA ready'].map(t => (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--slate-dim)' }}>
              <CheckCircle size={11} color="var(--teal)" />{t}
            </div>
          ))}
        </div>
      </section>

      {/* Why Quarterly */}
      <section style={{ borderTop: '1px solid var(--navy-border)', padding: 'clamp(48px,7vw,80px) clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <div style={{ fontSize: 11, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.14em', fontWeight: 700, marginBottom: 12 }}>Why Quarterly</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px,3.5vw,38px)', color: 'var(--white)', maxWidth: 540, margin: '0 auto' }}>
              Built for sole traders. Not finance teams.
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
            {[
              { title: 'Simple enough for anyone', body: 'Built for sole traders who do not want to wrestle with traditional accounting software. No jargon. No learning curve.', n: '01' },
              { title: 'Gets smarter as you use it', body: "As you categorise transactions, Quarterly learns your patterns. Repeat merchants are handled faster. Less manual work each quarter.", n: '02' },
              { title: 'Built around quarterly reporting', body: 'From bank feed to categorisation to submission — the whole workflow is designed around how sole traders actually need to work under MTD.', n: '03' },
              { title: 'Everything in one place', body: 'Transactions, receipts, reports and submissions. No switching between apps, no manual exports, no CSV files.', n: '04' },
            ].map((item, i) => (
              <div key={i} style={{
                background: 'var(--navy-card)',
                border: '1px solid var(--navy-border)',
                borderRadius: 14,
                padding: '22px 20px',
                position: 'relative',
                overflow: 'hidden',
                transition: 'border-color .2s, transform .2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,212,170,.3)'; e.currentTarget.style.transform = 'translateY(-2px)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.transform = '' }}
              >
                <div style={{ position: 'absolute', top: 14, right: 16, fontFamily: 'var(--font-display)', fontSize: 28, color: 'rgba(0,212,170,.08)', fontWeight: 800, lineHeight: 1 }}>{item.n}</div>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(0,212,170,.08)', border: '1px solid rgba(0,212,170,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <Zap size={16} color="var(--teal)" />
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, lineHeight: 1.3, color: 'var(--white)' }}>{item.title}</div>
                <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.75 }}>{item.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section style={{ borderTop: '1px solid var(--navy-border)', padding: 'clamp(48px,7vw,80px) clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 840, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ fontSize: 11, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.14em', fontWeight: 700, marginBottom: 12 }}>Who it's for</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px,3vw,34px)', color: 'var(--white)' }}>
              Sole traders across all kinds of work
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10 }}>
            {WHO.map((w, i) => (
              <div key={i} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 'var(--r)', padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--teal)', flexShrink: 0, marginTop: 6 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', marginBottom: 3 }}>{w.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--slate)', lineHeight: 1.5 }}>{w.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ borderTop: '1px solid var(--navy-border)', padding: 'clamp(48px,7vw,80px) clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ fontSize: 11, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.14em', fontWeight: 700, marginBottom: 12 }}>How it works</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px,3vw,34px)', color: 'var(--white)' }}>
              Live in under five minutes
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }} className="howitworks-grid">
            {[
              { n:'1', title:'Connect your bank', body:"Open Banking — read-only and secure. Your transactions start importing in under two minutes." },
              { n:'2', title:'Categorise as you go', body:"Business or personal. Quarterly AI handles the obvious ones — you confirm the rest. Most people finish a quarter in under 20 minutes." },
              { n:'3', title:'Watch your tax position update', body:"Your tax estimate recalculates in real time. You always know your number — not just at year end." },
              { n:'4', title:'Stay ready to submit', body:"When the deadline approaches, review your figures and submit directly to HMRC. No accountant required." },
            ].map(s => (
              <div key={s.n} style={{ display: 'flex', gap: 14, padding: '16px 18px', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 'var(--r)' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,212,170,.07)', border: '1px solid rgba(0,212,170,.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 12, color: 'var(--teal)' }}>{s.n}</span>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 5 }}>{s.title}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--slate)', lineHeight: 1.6 }}>{s.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ borderTop: '1px solid var(--navy-border)', padding: 'clamp(48px,7vw,80px) clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 660, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px,3vw,34px)' }}>Common questions</h2>
          </div>
          {FAQS.map((faq, i) => <PricingFaq key={i} faq={faq} />)}
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ borderTop: '1px solid var(--navy-border)', padding: 'clamp(56px,8vw,96px) clamp(16px,4vw,40px)', textAlign: 'center' }}>
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(26px,4vw,42px)', marginBottom: 14, lineHeight: 1.1 }}>
            Start today.<br />
            <span style={{ color: 'var(--teal)', fontStyle: 'italic' }}>£14.99/month.</span>
          </h2>
          <p style={{ fontSize: 14, color: 'var(--slate)', marginBottom: 32, lineHeight: 1.7 }}>
            No lock-in. No learning curve. Just a cleaner way to stay on top of quarterly reporting.
          </p>
          <Link to="/login" className="btn btn-primary btn-lg" style={{ gap: 9, fontSize: 16 }}>
            Start for £14.99/month <ArrowRight size={16} />
          </Link>
          <div style={{ fontSize: 12, color: 'var(--slate-dim)', marginTop: 12 }}>Cancel anytime · No card required to preview</div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid var(--navy-border)', padding: '22px clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily:'var(--font-display)', fontSize:11, color:'#08121F', fontWeight:700 }}>Q</span>
            </div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 14, color: 'var(--white)' }}>Quarterly</span>
          </Link>
          <div style={{ fontSize: 12, color: 'var(--slate-dim)' }}>Built by Chartered Accountants · © 2026</div>
          <div style={{ display: 'flex', gap: 18 }}>
            <Link to="/" style={{ fontSize: 12, color: 'var(--slate-dim)' }}>Home</Link>
            <Link to="/login" style={{ fontSize: 12, color: 'var(--slate-dim)' }}>Sign in</Link>
            <a href="mailto:mubarik@techedgeaccounting.com" style={{ fontSize: 12, color: 'var(--slate-dim)' }}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  )
}

// FAQ needs local state — extracted as sub-component
function PricingFaq({ faq }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ borderBottom: '1px solid var(--navy-border)' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 0', textAlign: 'left', gap: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)' }}>{faq.q}</span>
        <ChevronDown size={14} color="var(--slate)" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
      </button>
      {open && (
        <div style={{ paddingBottom: 18, fontSize: 14, color: 'var(--slate)', lineHeight: 1.8 }}>{faq.a}</div>
      )}
    </div>
  )
}
